import {createRoot} from 'react-dom/client'
import {PostHogErrorBoundary, PostHogProvider} from '@posthog/react'
import * as Sentry from '@sentry/react'
import './index.css'
import {runtimeConfig} from './config/runtime-config'
import {isDebug} from './lib/debug'
import {initializeSentry, instrumentationProvider} from './instrumentation'
import {ErrorFallback} from './components/ErrorFallback'
import {isTruthy} from "@/lib/utils.ts";

// [SEL-6] App entry point with Sentry.ErrorBoundary wrapping and SDK initialization
// Wrap initialization in async function to avoid top-level await
(async () => {
  // Load config before importing App to ensure Supabase client is created with correct settings
  try {
    await runtimeConfig.load();
    if (isDebug()) console.log('[main.tsx] Config loaded');
  } catch (error) {
    console.error('[main.tsx] Failed to load config:', error);
  }

  // Import App first
  const {default: App} = await import('./App.tsx');
  const container = document.getElementById("root")!;
  const root = createRoot(container);

  if (instrumentationProvider === "sentry") {
    // Initialize Sentry before React renders
    await initializeSentry();

    // Render with Sentry Error Boundary
    root.render(
      <Sentry.ErrorBoundary
        fallback={(errorData) => <ErrorFallback {...errorData} />}
        showDialog={false}
      >
        <App/>
      </Sentry.ErrorBoundary>
    );
  } else if (instrumentationProvider === "posthog") {
    // Initialize Posthog before React renders
    // await initializePosthog();
    const captureExceptions = isTruthy(runtimeConfig.config?.posthog?.enabled ?? true) && isTruthy(runtimeConfig.config?.posthog?.capture_exceptions ?? true);

    if (isDebug()) {
      console.log('[main.tsx] PostHog config:', {
        enabled: runtimeConfig.config?.posthog?.enabled,
        capture_exceptions: runtimeConfig.config?.posthog?.capture_exceptions,
        captureExceptions,
        host: runtimeConfig.config?.posthog?.host || import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      });
    }

    const options = {
      api_host: runtimeConfig.config?.posthog?.host || import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: '2025-05-24',
      capture_exceptions: captureExceptions,
    } as const

    // Render with Posthog Error Boundary
    root.render(
      <PostHogProvider apiKey={runtimeConfig.config?.posthog?.key || import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
        <PostHogErrorBoundary
          fallback={(errorData) => <ErrorFallback {...errorData} />}
        >
          <App/>
        </PostHogErrorBoundary>
      </PostHogProvider>
    );
  } else {
    // Render
    root.render(
      <App/>
    );
  }
})();
